import { Link } from "react-router-dom";
import { ArrowRight, HelpCircle } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { FAQS } from "@/lib/faqs";

/**
 * Reusable FAQ accordion section.
 *
 * Props:
 *   - title          (string)   Heading text
 *   - eyebrow        (string)   Small uppercase label above heading
 *   - subtitle       (string)   Optional paragraph under heading
 *   - limit          (number)   Render only the first N FAQs (default: all)
 *   - showSeeAllLink (boolean)  If true, append a "See all FAQs →" link to /faq
 *   - background     ("white"|"slate")  Section background style
 */
export default function FAQSection({
  title = "Frequently Asked Questions",
  eyebrow = "Answers",
  subtitle,
  limit,
  showSeeAllLink = false,
  background = "white",
}) {
  const items = typeof limit === "number" ? FAQS.slice(0, limit) : FAQS;
  const bgClass =
    background === "slate"
      ? "bg-slate-50 border-y border-slate-200"
      : "bg-white";

  return (
    <section
      data-testid="faq-section"
      className={`py-20 md:py-28 ${bgClass}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-blue-700">
            {eyebrow}
          </div>
          <h2 className="mt-3 font-display font-bold tracking-tight text-slate-900 text-3xl sm:text-4xl">
            {title}
          </h2>
          {subtitle && (
            <p className="mt-4 text-base text-slate-600 leading-relaxed">
              {subtitle}
            </p>
          )}
          <div className="mt-8 hidden lg:flex items-start gap-3 p-5 bg-slate-50 border border-slate-200 rounded-lg">
            <HelpCircle className="h-5 w-5 text-orange-600 mt-0.5 shrink-0" />
            <p className="text-sm text-slate-700 leading-relaxed">
              Have a question we didn't cover?{" "}
              <Link
                to="/quote"
                className="font-semibold text-blue-700 hover:text-blue-800 underline-offset-2 hover:underline"
              >
                Send it through our quote form
              </Link>{" "}
              and we'll get back to you within a day.
            </p>
          </div>
        </div>

        <div className="lg:col-span-8">
          <Accordion
            type="single"
            collapsible
            className="w-full divide-y divide-slate-200 border-y border-slate-200"
            data-testid="faq-accordion"
          >
            {items.map((item, i) => (
              <AccordionItem
                key={item.q}
                value={`faq-${i}`}
                data-testid={`faq-item-${i}`}
                className="border-b-0"
              >
                <AccordionTrigger
                  data-testid={`faq-trigger-${i}`}
                  className="py-5 text-left text-base sm:text-lg font-display font-semibold text-slate-900 hover:no-underline hover:text-blue-700 [&[data-state=open]]:text-blue-700"
                >
                  {item.q}
                </AccordionTrigger>
                <AccordionContent
                  data-testid={`faq-content-${i}`}
                  className="text-slate-600 leading-relaxed text-base pb-5 pr-8"
                >
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {showSeeAllLink && (
            <div className="mt-8">
              <Link
                to="/faq"
                data-testid="faq-see-all-link"
                className="inline-flex items-center gap-1 text-sm font-semibold text-blue-700 hover:text-blue-800 hover:gap-2 transition-all"
              >
                See all {FAQS.length} questions <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
